import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAudience } from '../context/AudienceContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, Pill, ArrowRight, Filter, X } from 'lucide-react';

import { API_URL } from '../config';

export default function MedicationsPage() {
  const { token } = useAuth();
  const { audience, isAdult } = useAudience();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterIndication, setFilterIndication] = useState('');

  useEffect(() => {
    fetchMedications();
  }, [audience, searchQuery, filterIndication]);

  const fetchMedications = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/medications?audience=${audience}&limit=50`;
      if (searchQuery) url += `&query=${encodeURIComponent(searchQuery)}`;
      if (filterIndication) url += `&indication=${encodeURIComponent(filterIndication)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMedications(data.medications || []);
      }
    } catch (error) {
      console.error('Error fetching medications:', error);
    } finally {
      setLoading(false);
    }
  };

  const indications = ['Arrêt cardiaque', 'Anaphylaxie', 'Douleur', 'Épilepsie', 'Choc', 'AVC', 'Infection'];

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Médicaments</h1>
        <p className="text-[hsl(var(--text-dim))]">Base de données {isAdult ? 'adulte' : 'pédiatrique'}</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted))]" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher par nom ou indication..."
          className="pl-10 bg-[hsl(var(--bg-elev))]"
          data-testid="medications-search"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={!filterIndication ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterIndication('')}
          className={!filterIndication ? 'bg-cyan-400/20 text-cyan-300 border-cyan-400/30' : ''}
        >
          Tous
        </Button>
        {indications.map((ind) => (
          <Button
            key={ind}
            variant={filterIndication === ind ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterIndication(filterIndication === ind ? '' : ind)}
            className={filterIndication === ind ? 'bg-cyan-400/20 text-cyan-300 border-cyan-400/30' : ''}
          >
            {ind}
          </Button>
        ))}
      </div>

      {/* Results */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="skeleton h-20 rounded-lg"></div>
          ))}
        </div>
      ) : medications.length === 0 ? (
        <div className="text-center py-12">
          <Pill className="w-12 h-12 text-[hsl(var(--muted))] mx-auto mb-4" />
          <p className="text-[hsl(var(--text-dim))]">Aucun médicament trouvé</p>
        </div>
      ) : (
        <div className="space-y-2">
          {medications.map((med) => (
            <Link
              key={med._id}
              to={`/medications/${med._id}`}
              className="med-card hover-lift flex items-center justify-between p-4 animate-fade-in"
              data-testid="medication-item"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-400/10 flex items-center justify-center flex-shrink-0">
                  <Pill className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-semibold">{med.name}</h3>
                  <p className="text-sm text-[hsl(var(--text-dim))]">{med.brand_name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {med.indications?.slice(0, 3).map((ind, i) => (
                      <Badge key={i} variant="secondary" className="text-xs bg-[hsl(var(--bg))] text-[hsl(var(--text-dim))]">
                        {ind}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-[hsl(var(--muted))]" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
